import React, { useState } from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Avatar, styled, CircularProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createAdmin } from '../services/apiservice';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';

const RedAsterisk = styled('span')({
  color: 'red',
});

const styles = {
  textCenter: {
    textAlign: 'center',
    color: '#666',
  },
  smallText: {
    fontSize: '0.8rem',
  },
};

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

const AdminSignup = () => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMessage('');
      setSuccessMessage('');
      setLoading(true);

      try {
        const response = await createAdmin(values.firstName, values.lastName, values.email, values.password);
        if (response.success && response.message === 'Admin created successfully') {
          setSuccessMessage('Admin Created Successfully');
          setTimeout(() => {
            navigate('/adminSignIn');
          }, 3000);
        } else {
          setErrorMessage('Admin already exists');
        }
      } catch (error) {
        console.error('Error creating admin:', error);
        if (error.response && error.response.data && error.response.data.message === 'Admin already exists') {
          setErrorMessage('An admin account with this email already exists, please try a different one.');
        } else {
          setErrorMessage('Error creating admin');
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      style={{
        height: '100vh',
        background: 'linear-gradient(-20deg, #616161 0%, #9bc5c3 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>Admin SignUp</title>
      </Helmet>
      <Container component={Paper} elevation={6} maxWidth="xs" style={{
        padding: 30,
        borderRadius: '15px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        position: 'relative',
        zIndex: 1,
      }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container justifyContent="center" alignItems="center">
            <Grid item>
              <Avatar
                src='/cirapng.png'
                alt="Logo"
                style={{
                  width: 80,
                  height: 100,
                  marginBottom: 15,
                  overflow: 'visible',
                }}
              />
            </Grid>
          </Grid>
          <Typography variant="h5" component="h2" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold', color: '#333', marginBottom: 5 }}>
            Admin Signup
          </Typography>
          <Typography variant="subtitle1" component="h2" gutterBottom style={{ ...styles.textCenter, ...styles.smallText, marginBottom: 15 }}>
            Join Credential Intelligence Recognition Assistant as an Admin
          </Typography>
          {errorMessage && (
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              style={{
                ...styles.textCenter,
                marginBottom: 10,
                color: 'red',
                animation: 'blink 2s infinite',
              }}
            >
              {errorMessage}
            </Typography>
          )}
          {successMessage && (
            <Typography
              variant="subtitle1"
              component="h2"
              gutterBottom
              style={{
                ...styles.textCenter,
                marginBottom: 10,
                color: 'green',
                animation: 'blink 2s infinite',
              }}
            >
              {successMessage}
            </Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='First Name'
                name="firstName"
                variant="outlined"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label='Last Name'
                name="lastName"
                variant="outlined"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Email'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="email"
                type="email"
                variant="outlined"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={
                  <>
                    {'Password'} <RedAsterisk>*</RedAsterisk>
                  </>
                }
                name="password"
                type="password"
                variant="outlined"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                style={{ marginBottom: 5 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                style={{
                  padding: 10,
                  height: '45px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
                disabled={formik.isSubmitting || loading}
              >
                <span>Sign Up</span>
                {(formik.isSubmitting || loading) && (
                  <CircularProgress color="inherit" size={24} />
                )}
              </Button>

            </Grid>
            <Grid item xs={12} style={{ textAlign: 'center', marginTop: 5 }}>
              <Typography
                variant="subtitle1"
                component="h2"
                gutterBottom
                style={{ ...styles.textCenter }}
              >
                Already have an account?{' '}
                <Button color="primary" onClick={() => navigate('/login')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                  Sign In
                </Button>
              </Typography>
            </Grid>
          </Grid>
        </form>
      </Container>
    </Box>
  );
};

export default AdminSignup;
